/**
 * pillow-live-texture.js — עדכון פני הכרית בלי לבנות אותה מחדש.
 *
 * ═══ מה זה פותר ═══
 *
 * generate3D עושה שני דברים שכרוכים היום יחד:
 *   1. גאומטריה — מעקב קונטור, convex hull, בניית mesh.  ~2,200ms
 *   2. טקסטורה  — gl.texImage2D אחד.                      ~1ms
 *
 * כיתוב על הכרית לא משנה את הצורה (הוא נשאר בתוך גבול ההדפסה), ולכן
 * אין שום סיבה לשלם 2,200ms על כל הקלדה. אנחנו כותבים פיקסלים חדשים
 * לתוך אותו אובייקט טקסטורה, קוראים ל-drawScene, וזהו.
 *
 * ═══ איך משיגים את הטקסטורה בלי לגעת במנוע ═══
 *
 * ב-custom-script.js הטקסטורות הן `let` ברמת הסקריפט ולא על window.
 * במקום לשנות את המנוע — עוטפים את gl.bindTexture ו-gl.texImage2D
 * ורואים אילו טקסטורות מקבלות HTMLCanvasElement. אלה, ורק אלה, הן
 * הטקסטורות של הכרית: תווית המותג מגיעה מ-Image, וטקסטורת הצבע היא
 * Uint8Array 1×1.
 *
 * ═══ ולמה זה לא מספיק, ומה כן ═══
 *
 * ניסיתי לזהות את הטקסטורות לפי סדר ההעלאה, ואחר כך לפי סדר הציור.
 * שתיהן נכשלו במדידה, ובאותה צורה מסוכנת:
 *
 *   generate3D מעלה את הטקסטורה בתחילת processImage, אבל משייך אותה
 *   לתצוגה רק ב-onComplete — אחרי כל עבודת הקונטור. כששתי בניות רצות
 *   בזו אחר זו (העלאה חדשה, שינוי צבע), קיבלנו טקסטורה של בנייה אחת
 *   בזמן שעל המסך הוצגה אחרת — והכתיבה שלנו החליפה את *התמונה של
 *   הכרית* בתמונה אחרת לגמרי. בדיוק מה שהמשתמש דיווח.
 *
 * המסקנה: הרכיב הזה לא יכול להכריע מי חזית ומי גב, כי הוא לא יודע
 * איזו תמונה נכונה. הוא חושף את המועמדות (candidates) עם עותק נקי של
 * כל אחת, והקורא — שמחזיק את ה-Processed Image — משווה ומכריע.
 * אם אף מועמדת לא מתאימה, לא כותבים כלום.
 *
 * ═══ הגבול ═══
 *
 * תקף *רק* כשהסילואט לא משתנה. שינוי צבע, גודל או תמונה מריצים
 * generate3D מלא — ואז המנוע מעלה בסיס חדש, אנחנו קולטים אותו,
 * ומורחים עליו את הכיתוב מחדש (onBase).
 *
 * ═══ שבירות ידועה ═══
 *
 * נשען על התנהגות פנימית של המנוע. שינוי עתידי באופן שבו הוא מעלה
 * טקסטורות ישבור את זה. לכן isReady() חייבת להיבדק, וכל קורא חייב
 * ליפול חזרה ל-generate3D כשמתקבל false.
 */
(function (global) {
  'use strict';

  var installed = false;
  var boundTex = null;
  var glRef = null;
  var writing = false;        // מונע מהכתיבות שלנו להיתפס כהעלאה של המנוע

  var isCanvasTex = new WeakSet();
  var counter = 0;

  /* רשומה לכל טקסטורה שקיבלה קנבס: העותק הנקי, סדר ההעלאה, והאם
     ראינו אותה נצבעת. מערך ולא WeakMap כי צריך *לעבור* על הרשומות —
     ראו candidates(). מוגבל בגודל כדי שלא נחזיק עותקים לנצח. */
  var tracked = [];
  var KEEP = 6;

  var baseListeners = [];

  function install() {
    if (installed) return;
    installed = true;

    var ctors = [global.WebGLRenderingContext, global.WebGL2RenderingContext];
    for (var i = 0; i < ctors.length; i++) {
      if (!ctors[i]) continue;
      wrap(ctors[i].prototype);
    }
  }

  function wrap(proto) {
    var origBind = proto.bindTexture;
    proto.bindTexture = function (target, tex) {
      if (target === this.TEXTURE_2D) { boundTex = tex; glRef = this; }
      return origBind.apply(this, arguments);
    };

    var origTexImage = proto.texImage2D;
    proto.texImage2D = function () {
      if (!writing && boundTex) {
        var src = arguments[arguments.length - 1];
        if (src && global.HTMLCanvasElement && src instanceof global.HTMLCanvasElement) {
          isCanvasTex.add(boundTex);
          noteUpload(boundTex, src);
        }
      }
      return origTexImage.apply(this, arguments);
    };

    ['drawElements', 'drawArrays'].forEach(function (name) {
      var orig = proto[name];
      if (!orig) return;
      proto[name] = function () {
        if (!writing && boundTex && isCanvasTex.has(boundTex)) noteDrawn(boundTex);
        return orig.apply(this, arguments);
      };
    });
  }

  function clone(canvas) {
    var c = document.createElement('canvas');
    c.width = canvas.width;
    c.height = canvas.height;
    c.getContext('2d').drawImage(canvas, 0, 0);
    return c;
  }

  function record(tex) {
    for (var i = 0; i < tracked.length; i++) if (tracked[i].tex === tex) return tracked[i];
    return null;
  }

  function noteUpload(tex, canvas) {
    var r = record(tex);
    if (!r) {
      r = { tex: tex, snapshot: null, seq: 0, drawn: false };
      tracked.push(r);
      if (tracked.length > KEEP) tracked.shift();
    }
    r.snapshot = clone(canvas);
    r.seq = ++counter;
    notifyBase();
  }

  function noteDrawn(tex) {
    var r = record(tex);
    /* ההודעה יוצאת רק כשמשהו *באמת* השתנה. קודם היא יצאה בכל פקודת
       ציור — עשרות פעמים בשנייה — והקורא איפס בכל פעם את זיהוי
       הפָּנים ושילם עליו מחדש. מדדתי 3,338ms על עדכון של מילישניות. */
    if (!r || r.drawn) return;
    r.drawn = true;
    notifyBase();
  }

  /**
   * כל הטקסטורות שקיבלו קנבס, עם העותק הנקי, סדר ההעלאה, והאם ראינו
   * אותן נצבעות.
   *
   * ═══ למה גם כאלה שעוד לא נצבעו ═══
   *
   * המנוע מצייר רק את הפן שפונה למצלמה. טקסטורת הגב מועלית מיד, אבל
   * נצבעת רק אחרי שמסובבים את הכרית — ולכן רשימה של "מה שנצבע" בלבד
   * מכילה חזיתות של כמה בניות ואף לא גב אחד. מדדתי בדיוק את זה, וזו
   * הסיבה שהכיתוב נצרב בפן הלא נכון.
   *
   * ═══ ולמה הרכיב הזה לא מחליט מי חזית ומי גב ═══
   *
   * הוא לא יודע איזו תמונה נכונה. רק הקורא — שמחזיק את ה-Processed
   * Image — יכול להשוות ולהכריע. כאן נשארת רק העובדה הגולמית.
   */
  function candidates() {
    var out = [];
    for (var i = tracked.length - 1; i >= 0; i--) {
      if (tracked[i].snapshot) out.push(tracked[i]);
    }
    return out;
  }

  /** כתיבה לטקסטורה מסוימת. הקורא בחר אותה, אנחנו רק כותבים. */
  function writeFace(tex, canvas) {
    if (!glRef || !tex || !canvas) return false;
    var gl = glRef;
    writing = true;
    try {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
      setParams(gl);
      return true;
    } catch (err) {
      console.warn('כתיבה לטקסטורה נכשלה:', err);
      return false;
    } finally {
      writing = false;
    }
  }

  function redraw() {
    if (typeof global.drawScene === 'function') { global.drawScene(); return true; }
    return false;
  }

  /* ההודעה נדחית לסוף המשימה: ברגע הזה המנוע עדיין באמצע processImage,
     והקורא עלול לרצות לצייר מיד — מה שהיה נכתב לתוך טקסטורה שהמנוע
     עומד לדרוס בשורה הבאה. */
  var notifyPending = false;
  function notifyBase() {
    if (notifyPending) return;
    notifyPending = true;
    setTimeout(function () {
      notifyPending = false;
      for (var i = 0; i < baseListeners.length; i++) {
        try { baseListeners[i](); } catch (err) { console.warn(err); }
      }
    }, 0);
  }

  function onBase(fn) { if (typeof fn === 'function') baseListeners.push(fn); }

  function isReady() {
    return !!(glRef && tracked.length && typeof global.drawScene === 'function');
  }

  function setParams(gl) {
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  }

  function stats() {
    var drawn = 0;
    for (var i = 0; i < tracked.length; i++) if (tracked[i].drawn) drawn++;
    return {
      hasGl: !!glRef,
      uploads: counter,
      drawn: drawn,
      snapshots: candidates().length
    };
  }

  global.PZLiveTexture = {
    install: install,
    isReady: isReady,
    onBase: onBase,
    candidates: candidates,
    writeFace: writeFace,
    redraw: redraw,
    clone: clone,
    stats: stats
  };

  install();
})(window);